import type { AppThunk } from "@Store/index";
import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import getUniversityPageData from "./getUniversityPageData";

export type CustomPageConfig = {
  pageTitle: string;
  pageDescription: string;
  photos: string[];
  sections: Array<{
    id: string;
    title: string;
    description: string;
    photos: string[];
    disabled: boolean;
  }>;
};

interface UniversityPageData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  slug: string;
  data: CustomPageConfig | null;
  enabled: boolean;
  error: any;
}

export const initialState: UniversityPageData = {
  readyStatus: "invalid",
  slug: "",
  data: null,
  enabled: false,
  error: null,
};

const universityPageSlice = createSlice({
  name: "universityPageData",
  initialState,
  reducers: {
    getUniversityPageRequesting: (
      state: UniversityPageData,
      { payload }: PayloadAction<string>,
    ) => {
      state.slug = payload;
      state.readyStatus = "request";
      if (__CLIENT__) window.scrollTo(0, 0);
    },
    getUniversityPageSuccess: (
      state: UniversityPageData,
      { payload }: PayloadAction<{ slug: string; config: CustomPageConfig; enabled: boolean }>,
    ) => {
      state.slug = payload.slug;
      state.data = payload.config;
      state.enabled = payload.enabled;
      state.readyStatus = "success";
      state.error = null;
    },
    getUniversityPageFailure: (state: UniversityPageData, { payload }: PayloadAction<any>) => {
      state.error = payload;
      state.readyStatus = "failure";
    },
    updateUniversityPageConfig: (
      state: UniversityPageData,
      { payload }: PayloadAction<{ config: CustomPageConfig; enabled: boolean }>,
    ) => {
      state.data = payload.config;
      state.enabled = payload.enabled;
      state.readyStatus = "success";
      state.error = null;
    },
  },
});

export const {
  getUniversityPageRequesting,
  getUniversityPageSuccess,
  getUniversityPageFailure,
  updateUniversityPageConfig,
} = universityPageSlice.actions;

export default universityPageSlice.reducer;

export const fetchUniversityPageDataIfNeeded =
  (props: any): AppThunk =>
  async (dispatch, getState: any) => {
    const state = getState();
    const pageData = state.universityPageData;
    const partner_uuid = state.whitelabelData?.partner_uuid;

    if (!partner_uuid) {
      return;
    }

    if (pageData.readyStatus === "success" && pageData.data) {
      if (state.router?.action === "PUSH" && __CLIENT__) {
        window.scrollTo(0, 0);
      }
      return;
    }

    await dispatch(getUniversityPageData(partner_uuid, props.staticContext));

    if (state.router?.action === "PUSH" && __CLIENT__) {
      window.scrollTo(0, 0);
    }
  };
